import React, { Suspense, useEffect, useRef, useState } from 'react';

const SectionFallback = ({ minHeight }) => (
  <div
    aria-hidden="true"
    style={{
      minHeight,
      background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.02), transparent)',
    }}
  />
);

/**
 * Keeps below-the-fold landing code out of the critical request path. The
 * generous root margin starts loading before the visitor reaches the section,
 * while the placeholder keeps hash/CTA navigation functional beforehand.
 */
const DeferredLandingSection = ({
  anchorId,
  component: Component,
  minHeight = 520,
  reveal = true,
}) => {
  const ref = useRef(null);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    if (!('IntersectionObserver' in window)) {
      setShouldRender(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setShouldRender(true);
        observer.disconnect();
      },
      { rootMargin: '800px 0px', threshold: 0 },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      id={shouldRender ? undefined : anchorId}
      className={reveal ? 'lp-deferred-section lp-fade-up visible' : 'lp-deferred-section'}
      style={shouldRender ? undefined : { minHeight }}
    >
      {shouldRender && (
        <Suspense fallback={<SectionFallback minHeight={minHeight} />}>
          <Component />
        </Suspense>
      )}
    </div>
  );
};

export default DeferredLandingSection;
